// main.service.ts - Agregar el método showError si no existe
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { WorkSheet, WorkBook, utils, write } from 'xlsx';

export interface NotificationOptions {
  title: string;
  message: string;
  icon: string;
  color: string;
}

@Injectable({
  providedIn: 'root'
})
export class MainService {
  private loading = new BehaviorSubject<boolean>(false);

  // Sujeto para el diálogo de confirmación
  private confirmActionSource = new Subject<{ message: string, onConfirm: () => void, onClose: () => void, confirmLabel?: string }>();
  confirmAction$ = this.confirmActionSource.asObservable();

  // Sujeto para el diálogo de notificación
  private notificationSource = new Subject<NotificationOptions>();
  notification$ = this.notificationSource.asObservable();

  // Sujeto para cerrar los diálogos
  private closeDialogsSource = new Subject<void>();
  closeDialogs$ = this.closeDialogsSource.asObservable();

  constructor() {}

  get loading$(): Observable<boolean> {
    return this.loading.asObservable();
  }

  ShowLoading(): void {
    console.log("show loading")
    this.loading.next(true);
  }

  HideLoading(): void {
    console.log("hide loading")
    this.loading.next(false);
  }

  showConfirmation(message: string, onConfirm: () => void, onClose?: () => void, confirmLabel: string = 'Confirmar') {
    this.confirmActionSource.next({
      message,
      onConfirm,
      onClose: onClose || (() => {}),
      confirmLabel
    });
  }

  showError(message: string, title: string = 'Error'): void {
    this.showNotificacionError(message, title);
  }

  showNotificacionExito(message: string, title: string = 'Éxito'): void {
    this.notificationSource.next({
      title,
      message,
      icon: 'pi pi-check-circle',
      color: 'text-verde-300'
    });
  }

  showNotificacionError(message: string, title: string = 'Error'): void {
    this.notificationSource.next({
      title,
      message,
      icon: 'pi pi-times-circle',
      color: 'text-rojo-300'
    });
  }

  showNotificacionCustom(options: NotificationOptions): void {
    this.notificationSource.next(options);
  }

  public async generateExcel(json_data: any, filename: any, worksheetname: any = "Sheet1") {
    const worksheet: WorkSheet = utils.json_to_sheet(json_data);
    const workbook: WorkBook = utils.book_new();

    // Calcular ancho de columnas automáticamente
    const columnWidths = this.calculateColumnWidths(json_data);
    worksheet['!cols'] = columnWidths;

    workbook.SheetNames.push(worksheetname);
    workbook.Sheets[worksheetname] = worksheet;
    const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'buffer' });

    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${filename}.xlsx`;
    link.click();
  }

  private calculateColumnWidths(data: any[]): any[] {
    if (!data.length) return [];

    const columnHeaders = Object.keys(data[0]);
    const sampleSize = Math.min(data.length, 100);
    const sampleData = data.slice(0, sampleSize);

    const columnWidths = columnHeaders.map(header => {
      const maxWidth = sampleData.reduce((max, row) => {
        const cellValue = row[header]?.toString() || '';
        return Math.max(max, cellValue.length);
      }, header.length);

      const lastRowValue = data[data.length - 1][header]?.toString().length || 0;
      const finalMax = Math.max(maxWidth, lastRowValue);

      return {
        wch: Math.min(
          Math.max(finalMax * 1.2, 10),
          100
        )
      };
    });

    return columnWidths;
  }

  public formatDate(date: Date | string | undefined): string {
    if (!date) return '';

    let d: Date;
    if (typeof date === 'string') {
      d = new Date(date);
    } else if (date instanceof Date) {
      d = date;
    } else {
      return '';
    }

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours() > 12 ? d.getHours() - 12 : d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    const t = d.getHours() > 11 ? 'PM' : 'AM';
    return `${day}/${month}/${year} ${hours}:${minutes} ${t}`;
  }
}
